'use client';

import {
  addToast,
  clearAllToasts,
  ErrorToast,
  SuccessToast,
} from '@otter-ai/den-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { SECOND_MS } from '@/constants/time';
import { queryKey as workspaceQueryKey } from '@/query/workspaceQueryCreator';

const TOAST_DURATION_MS = 5 * SECOND_MS;

import type { GetWorkspaceResponse } from '@otter-ai/recall-client';
import { useGetCurrentUser } from '@/shared/hooks/current-user/useGetCurrentUser';
import { openConfirmDialog } from '@/shared/modals/confirm/openConfirmDialog';
import { openDialog } from '@/shared/modals/openDialog';
import {
  type RevokeAccessAction,
  RevokeAccessDialog,
  type RevokeAccessResult,
} from './RevokeAccessDialog';
import {
  bulkDeactivateOrRemoveWorkspaceUser,
  bulkDeleteUsersFromWorkspace,
  bulkHideTeamMembers,
  bulkReactivateWorkspaceUsers,
  bulkUnhideTeamMembers,
  cancelInvitation as cancelInvitationApi,
  cancelMemberTrial,
  deactivateOrRemoveWorkspaceUser,
  hideTeamMember,
  leaveWorkspace as leaveWorkspaceApi,
  permanentlyDeleteWorkspaceUser,
  processJoinRequest,
  reactivateWorkspaceUser,
  resendInvitation as resendInvitationApi,
  setMemberNotetakerAccess,
  setMemberPrivilege,
  unhideTeamMember,
} from './teamMembersMutations';
import {
  trackAcceptTeamMemberSuccess,
  trackBulkActivateCancelled,
  trackBulkActivateConfirmed,
  trackBulkActivatePromptOpened,
  trackBulkDeleteCancelled,
  trackBulkDeleteConfirmed,
  trackBulkDeletePromptOpened,
  trackBulkHideCancelled,
  trackBulkHideConfirmed,
  trackBulkHidePromptOpened,
  trackBulkRevokeAccessCancelled,
  trackBulkRevokeAccessConfirmed,
  trackBulkRevokeAccessPromptOpened,
  trackBulkUnhideCancelled,
  trackBulkUnhideConfirmed,
  trackBulkUnhidePromptOpened,
  trackCancelInvitationCancelled,
  trackCancelInvitationClick,
  trackCancelInvitationConfirmed,
  trackCancelInvitationPromptOpened,
  trackCancelTrialClick,
  trackCancelTrialConfirmation,
  trackDeclineRequest,
  trackDeleteMemberCancelled,
  trackDeleteMemberConfirmed,
  trackDeleteMemberPromptOpened,
  trackDemoteToMember,
  trackHideMemberCancelled,
  trackHideMemberConfirmed,
  trackHideMemberPromptOpened,
  trackLeaveWorkspaceCancelled,
  trackLeaveWorkspaceConfirmed,
  trackLeaveWorkspacePromptOpened,
  trackNotetakerToggle,
  trackPromoteToAdmin,
  trackPromoteToOwner,
  trackReactivateMemberCancelled,
  trackReactivateMemberConfirmed,
  trackReactivateMemberPromptOpened,
  trackResendInvitation,
  trackRevokeAccessCancelled,
  trackRevokeAccessConfirmed,
  trackRevokeAccessPromptOpened,
  trackUnhideMemberCancelled,
  trackUnhideMemberConfirmed,
  trackUnhideMemberPromptOpened,
} from './tracking';
import {
  shouldShowWorkspaceInviteSettingDialog,
  WorkspaceInviteSettingDialog,
} from './WorkspaceInviteSettingDialog';

export type MemberInfo = {
  /** The workspace member relationship ID (id field from API) */
  relationshipId: number;
  /** The user's actual user ID (member_id field from API) */
  userId: number;
  name: string;
  email: string;
};

export type BulkActionInfo = {
  selectedIds: Set<number>;
  /** List of relationship IDs for bulk operations */
  relationshipIds: number[];
  /** List of user IDs for bulk delete */
  userIds: number[];
  emails: string[];
};

export type TeamMembersActions = {
  promoteToAdmin: (memberId: number) => void;
  demoteToMember: (memberId: number) => void;
  promoteToOwner: (memberId: number) => void;
  resendInvitation: (email: string) => void;
  cancelInvitation: (member: MemberInfo) => void;
  revokeAccess: (member: MemberInfo) => void;
  reactivate: (member: MemberInfo) => void;
  deleteMember: (member: MemberInfo) => void;
  approveRequest: (userId: number) => void;
  declineRequest: (userId: number) => void;
  cancelTrial: (member: MemberInfo) => void;
  hide: (member: MemberInfo) => void;
  unhide: (member: MemberInfo) => void;
  bulkHide: (info: BulkActionInfo) => void;
  bulkUnhide: (info: BulkActionInfo) => void;
  bulkRevokeAccess: (info: BulkActionInfo) => void;
  bulkActivate: (info: BulkActionInfo) => void;
  bulkDelete: (info: BulkActionInfo) => void;
  toggleNotetaker: (memberId: number, enabled: boolean) => void;
  leaveWorkspace: () => void;
};

/**
 * Hook that provides all action handlers for the TeamMembersTable.
 * Actions that require confirmation will show a dialog before executing.
 */
export function useTeamMembersActions({
  workspaceId,
  workspaceName,
  workspaceDomains = [],
  inviteWhenSharing = false,
  requestToJoinClaimedDomainWorkspace = false,
  salesAssisted = false,
  isEdu = false,
}: {
  workspaceId: number;
  workspaceName: string;
  /** Domain strings for the workspace (e.g., ['otter.ai']) */
  workspaceDomains?: string[];
  /** Current value of the invite_when_sharing setting */
  inviteWhenSharing?: boolean;
  /** Whether this is a claimed domain workspace with request-to-join enabled */
  requestToJoinClaimedDomainWorkspace?: boolean;
  /** Whether this is a sales-assisted workspace */
  salesAssisted?: boolean;
  /** Whether this is an edu workspace */
  isEdu?: boolean;
}): TeamMembersActions {
  const t = useTranslations('manage-team:team-profile');
  const queryClient = useQueryClient();
  const { userId } = useGetCurrentUser();
  const currentUserId = String(userId ?? '');

  // Hide "Remove from Workspace" option for claimed domain workspaces
  const showRemoveOption = !requestToJoinClaimedDomainWorkspace;

  // Default revoke action: 'deactivate' for sales-assisted non-edu workspaces, 'remove' otherwise
  const defaultRevokeAction: RevokeAccessAction =
    salesAssisted && !isEdu ? 'deactivate' : 'remove';

  // --- Role mutations ---
  const setPrivilegeMutation = useMutation({
    mutationFn: setMemberPrivilege,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      const message =
        variables.privilege === 'admin'
          ? t('toasts.success.promotedToAdmin')
          : variables.privilege === 'owner'
            ? t('toasts.success.promotedToOwner')
            : t('toasts.success.demotedToMember');
      addToast(
        <SuccessToast description={message} duration={TOAST_DURATION_MS} />,
      );
    },
    onError: (_, variables) => {
      clearAllToasts();
      const message =
        variables.privilege === 'admin'
          ? t('toasts.error.promoteToAdmin')
          : variables.privilege === 'owner'
            ? t('toasts.error.promoteToOwner')
            : t('toasts.error.demoteToMember');
      addToast(
        <ErrorToast description={message} duration={TOAST_DURATION_MS} />,
      );
    },
  });

  // --- Invitation mutations ---
  const resendInvitationMutation = useMutation({
    mutationFn: resendInvitationApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      addToast(
        <SuccessToast
          description={t('toasts.success.invitationResent')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.resendInvitation')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: cancelInvitationApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      addToast(
        <SuccessToast
          description={t('toasts.success.invitationCanceled')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.cancelInvitation')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  // --- Access mutations ---
  const deactivateOrRemoveMutation = useMutation({
    mutationFn: deactivateOrRemoveWorkspaceUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      addToast(
        <SuccessToast
          description={t('toasts.success.accessRevoked')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.revokeAccess')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  const bulkDeactivateOrRemoveMutation = useMutation({
    mutationFn: bulkDeactivateOrRemoveWorkspaceUser,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      const count = variables.emailList.split(',').length;
      addToast(
        <SuccessToast
          description={t('toasts.success.membersAccessRevoked', { count })}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.revokeAccessBulk')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: reactivateWorkspaceUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      addToast(
        <SuccessToast
          description={t('toasts.success.memberReactivated')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.reactivateMember')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  const bulkReactivateMutation = useMutation({
    mutationFn: bulkReactivateWorkspaceUsers,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      addToast(
        <SuccessToast
          description={t('toasts.success.membersReactivated', {
            count: variables.emails.length,
          })}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.reactivateMembers')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  // --- Delete mutations ---
  const deleteMutation = useMutation({
    mutationFn: permanentlyDeleteWorkspaceUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      addToast(
        <SuccessToast
          description={t('toasts.success.memberDeleted')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.deleteMember')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: bulkDeleteUsersFromWorkspace,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      const count = variables.userIdList.split(',').length;
      addToast(
        <SuccessToast
          description={t('toasts.success.membersDeleted', { count })}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.deleteMembers')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  // --- Hide mutations ---
  const hideMutation = useMutation({
    mutationFn: hideTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      addToast(
        <SuccessToast
          description={t('toasts.success.memberHidden')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.hideMember')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  const unhideMutation = useMutation({
    mutationFn: unhideTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      addToast(
        <SuccessToast
          description={t('toasts.success.memberUnhidden')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.unhideMember')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  const bulkHideMutation = useMutation({
    mutationFn: bulkHideTeamMembers,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      const count = variables.teamMemberIdList.split(',').length;
      addToast(
        <SuccessToast
          description={t('toasts.success.membersHidden', { count })}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.hideMembers')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  const bulkUnhideMutation = useMutation({
    mutationFn: bulkUnhideTeamMembers,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      const count = variables.teamMemberIdList.split(',').length;
      addToast(
        <SuccessToast
          description={t('toasts.success.membersUnhidden', { count })}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.unhideMembers')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  // --- Join request mutations ---
  const processJoinRequestMutation = useMutation({
    mutationFn: processJoinRequest,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      if (variables.approved) {
        trackAcceptTeamMemberSuccess({
          isNewUser: true,
          memberId: variables.userIds[0],
        });
      }
      addToast(
        <SuccessToast
          description={
            variables.approved
              ? t('toasts.success.requestApproved')
              : t('toasts.success.requestDeclined')
          }
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: (_, variables) => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={
            variables.approved
              ? t('toasts.error.approveRequest')
              : t('toasts.error.declineRequest')
          }
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  // --- Trial mutations ---
  const cancelTrialMutation = useMutation({
    mutationFn: cancelMemberTrial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      addToast(
        <SuccessToast
          description={t('toasts.success.trialCanceled')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.cancelTrial')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  // --- Notetaker mutations ---
  const setNotetakerAccessMutation = useMutation({
    mutationFn: setMemberNotetakerAccess,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: workspaceQueryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(workspaceQueryKey);

      // Optimistically update the cache
      queryClient.setQueryData(
        workspaceQueryKey,
        (old: GetWorkspaceResponse) => {
          if (!old) return old;
          const updated = structuredClone(old);
          const member = updated.workspace.members.find(
            (m) => m.member_id === variables.memberId,
          );
          if (member) {
            member.allow_virtual_assistant = variables.allowNotetaker;
          }
          return updated;
        },
      );

      return { previousData };
    },
    onSuccess: (_, variables) => {
      clearAllToasts();
      const message = variables.allowNotetaker
        ? t('toasts.success.notetakerEnabled')
        : t('toasts.success.notetakerDisabled');
      addToast(
        <SuccessToast description={message} duration={TOAST_DURATION_MS} />,
      );
    },
    onError: (_, __, context) => {
      // Rollback to previous value on error
      if (context?.previousData) {
        queryClient.setQueryData(workspaceQueryKey, context.previousData);
      }
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.toggleNotetaker')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
    onSettled: () => {
      // Refetch after mutation settles to ensure we have the latest server state
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  });

  // --- Leave workspace mutations ---
  const leaveWorkspaceMutation = useMutation({
    mutationFn: leaveWorkspaceApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      clearAllToasts();
      addToast(
        <SuccessToast
          description={t('toasts.success.leftWorkspace')}
          duration={TOAST_DURATION_MS}
        />,
      );
      // Redirect to home after leaving workspace
      window.location.href = '/';
    },
    onError: () => {
      clearAllToasts();
      addToast(
        <ErrorToast
          description={t('toasts.error.leaveWorkspace')}
          duration={TOAST_DURATION_MS}
        />,
      );
    },
  });

  return {
    promoteToAdmin: (memberId: number) => {
      trackPromoteToAdmin(memberId);
      setPrivilegeMutation.mutate({
        teamMemberId: memberId,
        teamId: workspaceId,
        privilege: 'admin',
      });
    },

    demoteToMember: (memberId: number) => {
      trackDemoteToMember(memberId);
      setPrivilegeMutation.mutate({
        teamMemberId: memberId,
        teamId: workspaceId,
        privilege: 'member',
      });
    },

    promoteToOwner: (memberId: number) => {
      trackPromoteToOwner(memberId);
      setPrivilegeMutation.mutate({
        teamMemberId: memberId,
        teamId: workspaceId,
        privilege: 'owner',
      });
    },

    resendInvitation: (email: string) => {
      trackResendInvitation();
      resendInvitationMutation.mutate({
        email,
        workspaceId,
        userId: currentUserId,
      });
    },

    cancelInvitation: async (member: MemberInfo) => {
      trackCancelInvitationClick();
      trackCancelInvitationPromptOpened();
      const confirmed = await openConfirmDialog({
        title: t('dialogs.revokeInvitation.title'),
        message: t.rich('dialogs.revokeInvitation.message', {
          email: member.email,
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        confirmText: t('dialogs.revokeInvitation.confirm'),
        cancelText: t('dialogs.common.cancel'),
        danger: true,
      });
      if (confirmed) {
        trackCancelInvitationConfirmed();
        cancelInvitationMutation.mutate({
          email: member.email,
          workspaceId,
          userId: currentUserId,
        });
      } else {
        trackCancelInvitationCancelled();
      }
    },

    revokeAccess: async (member: MemberInfo) => {
      trackRevokeAccessPromptOpened();
      const result = await openDialog<RevokeAccessResult>(async ({ done }) => (
        <RevokeAccessDialog
          done={done}
          memberName={member.name}
          memberEmail={member.email}
          workspaceName={workspaceName}
          showRemoveOption={showRemoveOption}
          defaultAction={defaultRevokeAction}
          isSalesAssisted={salesAssisted}
        />
      ));
      if (result?.confirmed) {
        trackRevokeAccessConfirmed(result.action);
        deactivateOrRemoveMutation.mutate({
          email: member.email,
          workspaceId,
          removeFromWorkspace: result.action === 'remove',
          userId: currentUserId,
        });
      } else {
        trackRevokeAccessCancelled();
      }
    },

    reactivate: async (member: MemberInfo) => {
      trackReactivateMemberPromptOpened();
      const confirmed = await openConfirmDialog({
        title: t('dialogs.reactivateMember.title'),
        message: t.rich('dialogs.reactivateMember.message', {
          name: member.name,
          email: member.email,
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        confirmText: t('dialogs.reactivateMember.confirm'),
        cancelText: t('dialogs.common.cancel'),
      });
      if (confirmed) {
        trackReactivateMemberConfirmed();
        reactivateMutation.mutate({
          email: member.email,
          workspaceId,
          userId: currentUserId,
        });
      } else {
        trackReactivateMemberCancelled();
      }
    },

    deleteMember: async (member: MemberInfo) => {
      trackDeleteMemberPromptOpened();
      const confirmed = await openConfirmDialog({
        title: t('dialogs.deleteMember.title'),
        message: (
          <div className="space-y-4">
            <p className="text-subtle text-sm font-medium">
              {t('dialogs.deleteMember.subtitle', { count: 1 })}
            </p>
            <div className="space-y-2">
              <p>
                {t.rich('dialogs.deleteMember.aboutToDelete', {
                  count: 1,
                  strong: (chunks) => <strong>{chunks}</strong>,
                })}
              </p>
              <p className="font-semibold">
                {member.name} ({member.email})
              </p>
            </div>
            <div className="rounded-lg bg-negative/10 p-3 text-negative">
              <p>{t('dialogs.deleteMember.warning', { count: 1 })}</p>
            </div>
            <p className="font-medium">
              {t.rich('dialogs.deleteMember.confirmSingle', {
                name: member.name,
                strong: (chunks) => <strong>{chunks}</strong>,
              })}
            </p>
          </div>
        ),
        danger: true,
        confirmText: t('dialogs.deleteMember.confirm'),
      });
      if (confirmed) {
        trackDeleteMemberConfirmed();
        deleteMutation.mutate({
          targetUserId: member.userId,
          workspaceId,
          userId: currentUserId,
        });
      } else {
        trackDeleteMemberCancelled();
      }
    },

    approveRequest: (userId: number) => {
      processJoinRequestMutation.mutate({
        workspaceId,
        userIds: [userId],
        approved: true,
      });
    },

    declineRequest: (userId: number) => {
      trackDeclineRequest(userId);
      processJoinRequestMutation.mutate({
        workspaceId,
        userIds: [userId],
        approved: false,
      });
    },

    cancelTrial: async (member: MemberInfo) => {
      trackCancelTrialClick();
      const confirmed = await openConfirmDialog({
        title: t('dialogs.cancelTrial.title'),
        message: t.rich('dialogs.cancelTrial.message', {
          name: member.name,
          email: member.email,
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        confirmText: t('dialogs.cancelTrial.confirm'),
        cancelText: t('dialogs.common.cancel'),
        danger: true,
      });
      if (confirmed) {
        trackCancelTrialConfirmation();
        try {
          await cancelTrialMutation.mutateAsync({
            workspaceId,
            memberId: member.userId,
          });

          // Show workspace invite setting dialog after successful cancel
          if (shouldShowWorkspaceInviteSettingDialog()) {
            await openDialog<void>(async ({ done }) => (
              <WorkspaceInviteSettingDialog
                done={done}
                workspaceId={workspaceId}
                workspaceDomains={workspaceDomains}
                currentInviteWhenSharing={inviteWhenSharing}
              />
            ));
          }
        } catch {
          // Error is handled by the mutation's error handling
        }
      }
    },

    hide: async (member: MemberInfo) => {
      trackHideMemberPromptOpened();
      const confirmed = await openConfirmDialog({
        title: t('dialogs.hideMember.title'),
        message: t.rich('dialogs.hideMember.message', {
          name: member.name,
          email: member.email,
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        confirmText: t('dialogs.hideMember.confirm'),
        cancelText: t('dialogs.common.cancel'),
      });
      if (confirmed) {
        trackHideMemberConfirmed();
        hideMutation.mutate({
          teamMemberId: member.relationshipId,
          teamId: workspaceId,
        });
      } else {
        trackHideMemberCancelled();
      }
    },

    unhide: async (member: MemberInfo) => {
      trackUnhideMemberPromptOpened();
      const confirmed = await openConfirmDialog({
        title: t('dialogs.unhideMember.title'),
        message: t.rich('dialogs.unhideMember.message', {
          name: member.name,
          email: member.email,
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        confirmText: t('dialogs.unhideMember.confirm'),
        cancelText: t('dialogs.common.cancel'),
      });
      if (confirmed) {
        trackUnhideMemberConfirmed();
        unhideMutation.mutate({
          teamMemberId: member.relationshipId,
          teamId: workspaceId,
        });
      } else {
        trackUnhideMemberCancelled();
      }
    },

    bulkHide: async ({ relationshipIds, emails }: BulkActionInfo) => {
      trackBulkHidePromptOpened(emails.length);
      const confirmed = await openConfirmDialog({
        title: t('dialogs.bulkHide.title'),
        message: (
          <div className="space-y-3">
            <p>{t('dialogs.bulkHide.message')}</p>
            <p className="font-semibold">{emails.join(', ')}</p>
          </div>
        ),
        confirmText: t('dialogs.bulkHide.confirm'),
        cancelText: t('dialogs.common.cancel'),
      });
      if (confirmed) {
        trackBulkHideConfirmed(emails.length);
        bulkHideMutation.mutate({
          teamMemberIdList: relationshipIds.join(','),
          teamId: workspaceId,
        });
      } else {
        trackBulkHideCancelled();
      }
    },

    bulkUnhide: async ({ relationshipIds, emails }: BulkActionInfo) => {
      trackBulkUnhidePromptOpened(emails.length);
      const confirmed = await openConfirmDialog({
        title: t('dialogs.bulkUnhide.title'),
        message: (
          <div className="space-y-3">
            <p>{t('dialogs.bulkUnhide.message')}</p>
            <p className="font-semibold">{emails.join(', ')}</p>
          </div>
        ),
        confirmText: t('dialogs.bulkUnhide.confirm'),
        cancelText: t('dialogs.common.cancel'),
      });
      if (confirmed) {
        trackBulkUnhideConfirmed(emails.length);
        bulkUnhideMutation.mutate({
          teamMemberIdList: relationshipIds.join(','),
          teamId: workspaceId,
        });
      } else {
        trackBulkUnhideCancelled();
      }
    },

    bulkRevokeAccess: async ({ emails }: BulkActionInfo) => {
      trackBulkRevokeAccessPromptOpened(emails.length);
      const result = await openDialog<RevokeAccessResult>(async ({ done }) => (
        <RevokeAccessDialog
          done={done}
          memberName=""
          memberEmail=""
          workspaceName={workspaceName}
          isBulk
          memberCount={emails.length}
          memberEmails={emails}
          showRemoveOption={showRemoveOption}
          defaultAction={defaultRevokeAction}
          isSalesAssisted={salesAssisted}
        />
      ));
      if (result?.confirmed) {
        trackBulkRevokeAccessConfirmed(emails.length, result.action);
        bulkDeactivateOrRemoveMutation.mutate({
          emailList: emails.join(','),
          workspaceId,
          removeFromWorkspace: result.action === 'remove',
        });
      } else {
        trackBulkRevokeAccessCancelled();
      }
    },

    bulkActivate: async ({ emails }: BulkActionInfo) => {
      trackBulkActivatePromptOpened(emails.length);
      const confirmed = await openConfirmDialog({
        title: t('dialogs.bulkReactivate.title'),
        message: (
          <div className="space-y-3">
            <p>{t('dialogs.bulkReactivate.message')}</p>
            <p className="font-semibold">{emails.join(', ')}</p>
          </div>
        ),
        confirmText: t('dialogs.bulkReactivate.confirm'),
        cancelText: t('dialogs.common.cancel'),
      });
      if (confirmed) {
        trackBulkActivateConfirmed(emails.length);
        bulkReactivateMutation.mutate({
          emails,
          workspaceId,
          userId: currentUserId,
        });
      } else {
        trackBulkActivateCancelled();
      }
    },

    bulkDelete: async ({ userIds, emails }: BulkActionInfo) => {
      trackBulkDeletePromptOpened(emails.length);
      const confirmed = await openConfirmDialog({
        title: t('dialogs.deleteMember.title'),
        message: (
          <div className="space-y-4">
            <p className="text-subtle text-sm font-medium">
              {t('dialogs.deleteMember.subtitle', { count: 2 })}
            </p>
            <div className="space-y-2">
              <p>
                {t.rich('dialogs.deleteMember.aboutToDelete', {
                  count: emails.length,
                  strong: (chunks) => <strong>{chunks}</strong>,
                })}
              </p>
              <p className="font-semibold">{emails.join(', ')}</p>
            </div>
            <div className="rounded-lg bg-negative/10 p-3 text-negative">
              <p>{t('dialogs.deleteMember.warning', { count: 2 })}</p>
            </div>
            <p className="font-medium">
              {t.rich('dialogs.deleteMember.confirmBulk', {
                count: emails.length,
                strong: (chunks) => <strong>{chunks}</strong>,
              })}
            </p>
          </div>
        ),
        danger: true,
        confirmText: t('dialogs.deleteMember.confirm'),
      });
      if (confirmed) {
        trackBulkDeleteConfirmed(emails.length);
        bulkDeleteMutation.mutate({
          userIdList: userIds.join(','),
        });
      } else {
        trackBulkDeleteCancelled();
      }
    },

    toggleNotetaker: (memberId: number, enabled: boolean) => {
      trackNotetakerToggle(memberId, enabled);
      setNotetakerAccessMutation.mutate({
        memberId,
        workspaceId,
        allowNotetaker: enabled,
      });
    },

    leaveWorkspace: async () => {
      trackLeaveWorkspacePromptOpened();
      const confirmed = await openConfirmDialog({
        title: t('dialogs.leaveWorkspace.title', { workspaceName }),
        message: (
          <ul className="list-disc space-y-2 pl-6 text-subtle">
            <li>{t('dialogs.leaveWorkspace.bullet1')}</li>
            <li>{t('dialogs.leaveWorkspace.bullet2')}</li>
            <li>{t('dialogs.leaveWorkspace.bullet3')}</li>
            <li>{t('dialogs.leaveWorkspace.bullet4')}</li>
          </ul>
        ),
        danger: true,
        confirmText: t('dialogs.leaveWorkspace.confirm'),
      });
      if (confirmed) {
        trackLeaveWorkspaceConfirmed();
        leaveWorkspaceMutation.mutate();
      } else {
        trackLeaveWorkspaceCancelled();
      }
    },
  };
}
